<template>
    <LoadingAnimation animation-style="bars" v-if="isStatusLoading"></LoadingAnimation>

    <div v-else class="w-100 user-modal-choose-payment-view px-4">
        <div v-if="isLegacy() === true" style="max-width: 607px;">
            <div class="alert alert-warning gap-10 d-flex flex-column align-items-center p-5">
                <p class="font-size-sm-200">{{ translate("You're still on a legacy subscription plan") }}</p>
                <p class="font-size-sm-125">{{ translate("Your legacy subscription does not allow to buy \"Pay As You Go\" Credits.") }}</p>
                <p class="font-size-sm-125">{{ translate("Please contact our support team to cancel your legacy subscription now and get a partial refund.") }}</p>
                <p class="font-size-sm-125">{{ translate("You can then choose one of our new subscription plans and get additional \"Pay As You Go\" Credits as needed.") }}</p>
                <button class="btn btn-lg contact-btn" @click="openContactSupport">
                    <span>{{ translate("Contact support") }}</span>
                </button>
            </div>
        </div>
        <div v-if="isLegacy() === false">
        <div v-if="!showWeekPass" class="mb-5 text-center">
            <p class="font-weight-bold font-size-200 mb-0">
                {{ titleText }}
            </p>
        </div>

        <TrialInfoGraphic
            v-if="isTrialEnabled"
            class="mb-4"
            :count-days-free-trial-duration="trialData.countDaysFreeTrialDuration"
            :count-days-reminder-before-trial-end="trialData.countDaysReminderBeforeTrialEnd"
            :date-first-charge-full="trialData.dateFirstChargeFull"
            :date-first-charge-short="trialData.dateFirstChargeShort"
            :price-at-trial-start="trialData.priceAtTrialStart[currency]"
        ></TrialInfoGraphic>

        <SelectFileSize v-if="isOc" :disabled="isSubmitting" class="mb-5"></SelectFileSize>

        <SelectConversionMinutes v-if="isApi2Convert" :disabled="isSubmitting" class="mb-5"></SelectConversionMinutes>

        <SelectCredits v-if="isCreditModal" :disabled="isSubmitting" class="mb-5"></SelectCredits>

        <SelectBilling
            v-if="!isApi2Convert && !isCreditModal"
            :disabled="isSubmitting"
            :absolute-file-size-limit="largestPurchasableFileSize"
            :trial-data="trialData"
            :currency="currency"
            :selected-size="selectedSize"
            :selected-provider="selectedProvider"
        ></SelectBilling>

        <hr />

        <div class="mb-1 text-center" v-html="dueText"></div>
        <div class="mb-3 text-center" v-if="!isCreditModal">
            <span class="mr-2_" v-if="this.selectedProduct === 'payg'"><i class="far fa-check text-success mr-1_"></i>{{ translate('Credits valid for one year') }}</span>
            <span class="mr-2_" v-if="this.selectedProduct === 'subscription'"><i class="far fa-check text-success mr-1_"></i>{{ translate('Cancel anytime') }}</span>
            <span><i class="far fa-check text-success mr-1_"></i>{{ translate('Full money back guarantee') }}</span>
        </div>

        <ButtonWithLoadingState
            data-user-modal-button-submit
            class="btn btn-success btn-lg btn-block font-weight-bold text-uppercase rounded mb-2"
            icon="fa fa-arrow-right lh-1"
            :text="submitButtonText"
            :is-state-loading="isSubmitting"
            :disabled="!productSlug"
            :show-icon-after-text="true"
            @click.stop.prevent="goToChoosePayment"
        ></ButtonWithLoadingState>
        <div v-show="showGoBack">
            <span
                @click.stop.prevent="goBack"
                class="w-100 d-flex justify-content-center align-items-center text-dark font-size-125 cursor-pointer"
                ><i class="fa fa-arrow-left lh-1 mr-1 text-uppercase"></i>{{ translate('Back') }}</span
            >
        </div>
        <Transition name="fade-fast">
            <div
                v-if="showCancelInfo || showRefundInfo"
                @click.stop.prevent="
                    showRefundInfo = false;
                    showCancelInfo = false;
                "
                class="position-absolute w-100 h-100 top-0 left-0 bg-modal"
                style="z-index: 99; box-shadow: 0 0 2000px 2000px rgb(0 0 0 / 50%)"
            ></div>
        </Transition>

        <div class="w-100 position-relative">
            <Transition name="fade-grow-slow">
                <div
                    class="border px-4 py-3 bg-white rounded position-absolute w-100 bottom-0 shadow"
                    v-if="showRefundInfo"
                    style="max-width: 404px; z-index: 100; left: 50%; transform: translate(-50%, 0)"
                >
                    <p class="font-size-125 border-bottom font-weight-bold pb-2 mb-2 position-relative">
                        {{ translate('Refund policy') }}
                        <span
                            data-user-modal-payment-refund-info-close
                            @click.stop.prevent="showRefundInfo = false"
                            class="cursor-pointer position-absolute top-0 right-0 lh-1 font-size-150"
                            >&times;</span
                        >
                    </p>
                    <p>
                        {{
                        translate('You can request a full refund within 14 days after you subscribed with no questions asked. Please contact our support team for assistance.')
                        }}
                    </p>
                    <p class="mb-0">
                        {{ translate('Please note that you are not eligible for a refund anymore after 14 days.') }}
                    </p>
                </div>
            </Transition>

            <Transition name="fade-grow-slow">
                <div
                    class="border px-4 py-3 bg-white rounded position-absolute w-100 bottom-0 shadow"
                    v-if="showCancelInfo"
                    style="max-width: 404px; z-index: 100; left: 50%; transform: translate(-50%, 0)"
                >
                    <p class="font-size-125 border-bottom font-weight-bold pb-2 mb-2 position-relative">
                        {{ translate('Cancel the subscription at any time') }}
                        <span
                            data-user-modal-payment-cancel-info-close
                            @click.stop.prevent="showCancelInfo = false"
                            class="cursor-pointer position-absolute top-0 right-0 lh-1 font-size-150"
                            >&times;</span
                        >
                    </p>
                    <p>{{ translate('In order to cancel your subscription, please login and go to your dashboard.') }}</p>
                    <p class="mb-0">
                        {{
                        translate('You will be able to use the service after cancellation for the amount of time you have already paid for. You can reactivate your subscription later at any time.')
                        }}
                    </p>
                </div>
            </Transition>
        </div>

        <!-- min-height 45px to prevent bouncing around on oc when switching between daily pass and subscriptions -->
    </div>
    </div>
</template>

<script>
import ButtonWithLoadingState from '../../../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import TransFilter from '../../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import { userModalRoute } from '../router';
import SelectFileSize from '../components/SelectFileSize';
import SelectConversionMinutes from '../components/SelectConversionMinutes';
import SelectCredits from '../components/SelectCredits';
import { translate } from '../../../../../qaamgo/helper/translate';
import TrialInfoGraphic from '../components/TrialInfoGraphic';
import SelectBilling from '../components/SelectBilling';
import ChoosePayment from '../components/ChoosePayment';
import { paymentManager } from '../../../../payment/payment-manager';
import { projectHelper } from '../../../../misc/project-helper';
import { pageStoreMixin } from '../../../../store/page-store';
import { testIsNonEmptyString, testIsTrueish } from '../../../../../lib/test-and-assert/test-base';
import { getLocalizedDateString } from '../../../../../lib/localization/date-helper';
import { satGlobals } from '../../../../../qaamgo/sat/helper/sat-globals';
import { globalLogger } from '../../../../../qaamgo/helper/global-logger';
import { pageNotification } from '../../../../../qaamgo/helper/notify';
import { paymentApi } from '../../../../payment/payment-api';
import DataStatus from '../../../../../qaamgo/modules/sat/components/mixins/DataStatus';
import LoadingAnimation from '../../../../../qaamgo/modules/sat/components/LoadingAnimation';

export default {
    name: 'RegisterChooseBillingView',
    components: {
        LoadingAnimation,
        ChoosePayment,
        SelectBilling,
        TrialInfoGraphic,
        SelectConversionMinutes,
        SelectCredits,
        SelectFileSize,
        ButtonWithLoadingState,
    },
    mixins: [TransFilter, DataStatus, pageStoreMixin],
    created() {
        if (paymentManager.isPricingInfoInitialized()) {
            this.initializeData();
            this.setStatusDone();
            this.store.userPaymentModal.setSubmitting(false);

            return;
        }

        paymentApi
            .getPricingInformation(true)
            .then((pricingInformation) => {
                paymentManager.setConfigFromPricingInformation(pricingInformation);

                this.initializeData();
                this.setStatusDone();
                this.store.userPaymentModal.setSubmitting(false);
            })
            .catch((error) => {
                pageNotification.showGenericErrorMessage();

                globalLogger.log('user modal - choose payment - getPricingInformation - fail', error);
            });
    },
    computed: {
        /** @return {User} */
        storeUser() {
            return this.store.user.getUser;
        },
        /** @return {Boolean} */
        isTrialEnabled() {
            return this.store.userPaymentModal.offerTrial;
        },
        selectedPlan() {
            return this.store.userPaymentModal.selectedPlan;
        },
        selectedProduct() {
            return this.store.userPaymentModal.selectedProduct;
        },
        selectedSize() {
            return this.store.userPaymentModal.selectedSize;
        },
        selectedProvider() {
            return this.store.userPaymentModal.selectedProvider;
        },
        selectedBilling() {
            return this.store.userPaymentModal.selectedBilling;
        },
        selectedQuota() {
            return this.store.userPaymentModal.selectedQuota;
        },
        /** @return {Boolean} */
        isSubmitting() {
            return this.store.userPaymentModal.isSubmitting;
        },
        isSale() {
            return this.store.userPaymentModal.isSale;
        },
        currency() {
            return this.store.userPaymentModal.currency;
        },
        showGoBack() {
            return this.store.userPaymentModal.canGoBack;
        },
        productSlug() {
            if (projectHelper.isOnlineConvert() && this.selectedBilling === 'day') {
                const sizeInt = Number.parseInt(this.selectedSize);

                if (sizeInt < 1000) {
                    // ignore invalid in-between state - state should be fixed in next frame
                    return '';
                }
            }

            /** @type {PaymentData} */
            const paymentData = {};

            paymentData.billing = this.selectedBilling;
            if (this.isCreditModal) {
                paymentData.credits = this.selectedSize;
            } else {
                if (projectHelper.isOnlineConvert() || projectHelper.isApi2Convert()) {
                    paymentData.product = this.selectedSize;
                } else {
                    paymentData.product = this.selectedProduct;
                }
            }
            if (satGlobals.isCreditsInAllSatsEnabled()) {
                paymentData.quota = this.selectedQuota;
            }
            paymentData.provider = this.selectedProvider;
            return paymentManager.getSlugHelper().getSlug(paymentData, this.currency);
        },
        productPriceString() {
            if (!this.productSlug) {
                return '-';
            }

            return paymentManager.getSlugHelper().getPriceStringFromSlug(this.productSlug);
        },
        titleText() {
            if (this.isTrialEnabled) {
                return translate('Try %count% days free', { '%count%': this.trialData.countDaysFreeTrialDuration });
            }

            return translate('Choose Your Plan');
        },
        submitButtonText() {
            if (this.isTrialEnabled) {
                return translate('Try %count% days free', { '%count%': this.trialData.countDaysFreeTrialDuration });
            }

            return translate('Choose Payment');
        },
        goBackButtonText() {
            return translate('Go Back');
        },
        isCreditModal() {
            return satGlobals.isCreditsEnabled() && this.storeUser.isPremium();
        },
        dueText() {
            if (this.isTrialEnabled) {
                return translate('Total due in free trial: <strong>%amount%</strong>', {
                    '%amount%': this.trialData.priceAtTrialStart[this.currency],
                });
            }

            if (this.isSale && this.selectedBilling === 'annual_%currency%_%version%_sale_cyberweek') {
                return translate(
                    'Total due: <span style="text-decoration: line-through">%amount%</span> <strong>%salePrice%</strong>',
                    {
                        '%amount%': this.getPriceStringForBillingAndProduct('annual'),
                        '%salePrice%': this.productPriceString,
                    }
                );
            }

            return translate('Total due: <strong>%amount%</strong>', { '%amount%': this.productPriceString });
        },
    },
    methods: {
      getPricingLink() {
        return satGlobals.getPricingUrl();
      },
      isLegacy() {
        let user =  this.store.user.getUser;
        if (projectHelper.isImg2go() === true && user.isLegacySubscription() === true) {
          return true;
        }

        return false;
      },
        initializeData() {
            if (!testIsNonEmptyString(this.selectedProvider)) {
                this.store.userPaymentModal.setSelectedProvider('stripe');
            }

            if (this.isApi2Convert) {
                if (!testIsNonEmptyString(this.selectedSize)) {
                    this.store.userPaymentModal.setSelectedSize('500');
                }

                if (!testIsNonEmptyString(this.selectedBilling)) {
                    this.store.userPaymentModal.setSelectedBilling('day');
                }

                return;
            }

            if (this.isCreditModal) {
                if (!testIsNonEmptyString(this.selectedSize)) {
                    this.store.userPaymentModal.setSelectedSize('250');
                }

                if (!testIsNonEmptyString(this.selectedBilling)) {
                    this.store.userPaymentModal.setSelectedBilling('day');
                }

                return;
            }

            if (this.isOc && !testIsNonEmptyString(this.selectedSize)) {
                this.store.userPaymentModal.setSelectedSize('200');
            }

            if (!testIsNonEmptyString(this.selectedBilling) && !this.isSale) {
                this.store.userPaymentModal.setSelectedBilling('annual');
            } else if (!testIsNonEmptyString(this.selectedBilling) && this.isSale) {
                this.store.userPaymentModal.setSelectedBilling('annual_%currency%_%version%_sale_cyberweek');
            }

            if (!testIsNonEmptyString(this.selectedBilling) && (this.isOc || this.isPdf2Go) && this.isSale) {
                this.store.userPaymentModal.setSelectedBilling('annual_%currency%_%version%_sale_cyberweek');
            }

            this.trialData = paymentManager.getTrialData();

            this.trialData.countDaysFreeTrialDuration = satGlobals.getFreeTrialPeriod();

            const firstChargeDateObject = new Date();
            firstChargeDateObject.setDate(firstChargeDateObject.getDate() + this.trialData.countDaysFreeTrialDuration);

            const isFromEurope = testIsTrueish(this.storeUser?.getExtraKey('europe'));

            this.trialData.dateFirstChargeShort = getLocalizedDateString(
                satGlobals.getLocale(),
                firstChargeDateObject,
                { month: 'long', day: 'numeric' },
                isFromEurope
            );

            this.trialData.dateFirstChargeFull = getLocalizedDateString(
                satGlobals.getLocale(),
                firstChargeDateObject,
                { year: 'numeric', month: 'long', day: 'numeric' },
                isFromEurope
            );

            this.largestPurchasableFileSize = paymentManager.getLargestPurchasableFileSize();
        },
        getTranslatedSubmitButtonText() {
            if (this.isApi2Convert || this.selectedBilling === 'day' || this.selectedBilling === 'week') {
                return translate(
                    `By clicking on “Pay now”, your payment method will get charged with an amount of %AMOUNT%. <a id=\"refundLink\">Refunds</a> can be requested within 14 days, no questions asked.`,
                    { '%AMOUNT%': this.productPriceString }
                );
            }

            if (this.isTrialEnabled && this.selectedBilling === 'annual') {
                return translate(
                    `By clicking on “Try %trialDays% days free”, you agree that we will automatically continue your subscription after the end of the free trial (%date%) and then your payment method will get charged with a recurring annual amount of %amount%. You can <a id=\"cancelLink\">cancel the payment</a> at any time.`,
                    {
                        '%trialDays%': this.trialData.countDaysFreeTrialDuration,
                        '%date%': this.trialData.dateFirstChargeFull,
                        '%amount%': this.productPriceString,
                    }
                );
            }

            if (this.isTrialEnabled && this.selectedBilling === 'month') {
                return translate(
                    `By clicking on “Try %trialDays% days free”, you agree that we will automatically continue your subscription after the end of the free trial (%date%) and then your payment method will get charged with a recurring monthly amount of %amount%. You can <a id=\"cancelLink\">cancel the payment</a> at any time.`,
                    {
                        '%trialDays%': this.trialData.countDaysFreeTrialDuration,
                        '%date%': this.trialData.dateFirstChargeFull,
                        '%amount%': this.productPriceString,
                    }
                );
            }

            if (this.selectedBilling === 'annual') {
                return translate(
                    `By clicking on “Pay now”, your payment method will get charged with a recurring annual amount of %amount%. You can <a id=\"cancelLink\">cancel the payment</a> at any time. <a id=\"refundLink\">Refunds</a> can be requested within 14 days, no questions asked.`,
                    { '%amount%': this.productPriceString }
                );
            }

            if (this.selectedBilling === 'month') {
                return translate(
                    `By clicking on “Pay now”, your payment method will get charged with a recurring monthly amount of %amount%. You can <a id=\"cancelLink\">cancel the payment</a> at any time. <a id=\"refundLink\">Refunds</a> can be requested within 14 days, no questions asked.`,
                    { '%amount%': this.productPriceString }
                );
            }

            return '';
        },
        onBottomTextClicked(event) {
            const isRefundLinkClicked = event?.target?.id === 'refundLink';
            const isCancelLinkClicked = event?.target?.id === 'cancelLink';
            if (!isRefundLinkClicked && !isCancelLinkClicked) {
                return;
            }

            if (isRefundLinkClicked) {
                this.showRefundInfo = !this.showRefundInfo;

                if (this.showRefundInfo) {
                    this.showCancelInfo = false;
                }

                return;
            }

            if (isCancelLinkClicked) {
                this.showCancelInfo = !this.showCancelInfo;

                if (this.showCancelInfo) {
                    this.showRefundInfo = false;
                }
            }
        },
        getFormattedConversionMinutes(minutes) {
            const addedDot = minutes.replace(/(0{3}){0,1}(0{3}){1,}$/, '.$1.$2');

            // remove double dot
            return addedDot.replace(/\.+/, '.');
        },
        goToChoosePayment() {
            globalLogger.log('user modal - product selected - ' + this.productSlug);

            this.$router.push({
                name: userModalRoute.REGISTER_CHOOSE_PAYMENT,
            });
        },
        goBack() {
            this.$router.push({
                name: userModalRoute.REGISTER_PLAN_CREDITS_SELECT,
            });
        },
        getPriceStringForBillingAndProduct(billing, product) {
            /** @type {PaymentData} */
            const paymentData = {};

            paymentData.billing = billing;
            paymentData.product = product ?? this.selectedSize;
            paymentData.provider = this.selectedProvider;

            return paymentManager.getSlugHelper().getPriceString(paymentData, this.currency);
        },
        openContactSupport() {
            globalLogger.log('user modal - legacy user - contact form');

            this.$router.push({
                name: userModalRoute.CONTACT_US_FORM,
                params: {
                    defaultQuestionType: 'sales',
                },
            });
        }
    },
    data() {
        return {
            userModalRoute: userModalRoute,
            showRefundInfo: false,
            showCancelInfo: false,
            trialData: {},
            isOc: projectHelper.isOnlineConvert(),
            isPdf2Go: projectHelper.isPdf2go(),
            isApi2Convert: projectHelper.isApi2Convert(),
            largestPurchasableFileSize: '',
            showWeekPass: satGlobals.isWeekPassEnabled(),
        };
    },
};
</script>

<style lang="scss">
@use '~sass-material-colors/sass/sass-material-colors';

@use '../../../../../../scss/abstracts/mixins';
@use '../../../../../../scss/abstracts/functions';
@use '../../../../../../scss/abstracts/variables' as *;
@use 'sat-styles/abstracts/variables' as *;

.user-modal-choose-payment-view {
    max-width: 510px;
}

#cancelLink,
#refundLink {
    color: $link-color;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: $link-hover-color;
        text-decoration: none;
    }
}
</style>
